<script>
import { storeToRefs } from "pinia";
import { useCartStore } from "@/stores/cart";
import { useWorkingDaysStore } from "@/stores/workingdays";
import { useAddressStore } from "@/stores/address";
import { useAuthStore } from "@/stores/auth";
import { useWalletStore } from "@/stores/wallet";
import { useTranslationStore } from "@/stores/translation";
import { useCircleStore } from "@/stores/circle";
import { useMenuStore } from "@/stores/menu";
import AddAddress from "@/components/AddAddress.vue";
import { onUpdated, computed, onMounted, onBeforeUnmount, ref, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useSEO } from "@/composables/useSEO";
import { stripePromise } from "@/stripe.js";
import { trackBeginCheckout, trackPurchase, checkoutTracking, contentId } from "@/analytics/track";
import caxios from "../../axios";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

export default {
	name: "CheckoutPage",
	components: {
		AddAddress,
	},
	setup() {
		const cartStore = useCartStore();
		const workingDaysStore = useWorkingDaysStore();
		const addressStore = useAddressStore();
		const authStore = useAuthStore();
		const walletStore = useWalletStore();
		const translationStore = useTranslationStore();
		const menuStore = useMenuStore();

		// Translation helper
		const t = (label, modules, fallback = null) => {
			return translationStore.translate(label, modules, fallback);
		};

		// SEO Meta Tags with noindex (private page)
		useSEO({
			title: computed(() => t("seo_title", ["checkout"]) || "Checkout - Chi Hun Da Su"),
			description: computed(() => t("seo_description", ["checkout"]) || "Complete your order"),
			noindex: true, // Don't index checkout pages
			path: "/checkout",
		});

		// GA4-shaped line items for the purchase event (item_id/item_name/price/quantity).
		const purchaseItems = () =>
			(cartStore.cart || []).map((l) => ({
				item_id: contentId({ kind: 'item', sku: l.sku, variant_id: l.selected_meal_portion_id, meal_id: l.meal_id }),
				item_name: l.meal_name + (l.selected_meal_portion_name ? " - " + l.selected_meal_portion_name : ""),
				price: Number(l.selected_meal_portion_price || 0) + Number(l.selected_meal_addon_price || 0),
				quantity: l.quantity || 1,
			}));

		// Track begin-checkout to first-party analytics + all configured pixels
		onMounted(() => {
			nextTick(() => {
				const checkoutValue = TotalOrderPrice.value + (deliveryFee.value || 0) - TotalDiscount.value;
				trackBeginCheckout({
					value: checkoutValue,
					items: purchaseItems(),
					numItems: totalQty.value,
					meta: { num_items: totalQty.value },
				});
			});
			// Auto-apply a promo left by the /box landing page (e.g. FIRSTBOX).
			nextTick(() => {
				try {
					const promo = localStorage.getItem("chds_promo");
					if (promo) {
						localStorage.removeItem("chds_promo");
						if (!appliedCoupon.value && totalQty.value > 0) {
							couponCode.value = promo;
							validateAndApplyCoupon();
						}
					}
				} catch (e) { /* noop */ }
			});
		});

		const { user } = storeToRefs(authStore);
		// Guard for guests (not logged in) so the page doesn't crash before the
		// guest-details gate; billing is optional for card payments anyway.
		const billing_address = user.value ? authStore.getBillingAddress() : "";
		const billing_object = user.value?.billing_address || {};
		const { cart, totalQty, TotalOrderPrice, TotalDiscount, appliedCoupon } = storeToRefs(cartStore);
		const { delivery_time_slots, pickup_time_slots, schedule_date, schedule_time } = storeToRefs(workingDaysStore);

		/* If this basket came from Find My Meals, tell that quiz its order landed. Fire
		   and forget: it only records attribution, so a failure must never affect an
		   order that has already been created and paid. */
		const attachFindMyMealsOrder = (orderId) => {
			try {
				const token = localStorage.getItem("chds_fmm_token");
				if (!token || !orderId) return;
				caxios.post("/find-my-meals/attach-order/", { token, order_id: orderId })
					.catch(() => {})
					.finally(() => { try { localStorage.removeItem("chds_fmm_token"); } catch (e) { /* noop */ } });
			} catch (e) { /* noop */ }
		};

		// Slots are short codes ("A", "B") — show the delivery window next to them.
		const slotOptionLabel = (slot) => {
			const fmt = (v) => (v || "").slice(0, 5);
			if (slot && slot.start_time && slot.end_time) return `${slot.name} (${fmt(slot.start_time)}\u2013${fmt(slot.end_time)})`;
			return slot ? slot.name : "";
		};
		const { eligibleAddress, pickUpAddresses } = storeToRefs(addressStore);
		const hasAnyAddress = computed(() => (addressStore.addresses || []).length > 0);
		// A brand-new account has NO addresses — that's an instruction, not an error.
		const firstAddressHint = computed(() => translationStore.translate(
			"add_first_address_hint", ["checkout"],
			translationStore.currentLanguage === "zh"
				? "请先添加您的送货地址（点击下方\u201C添加新地址\u201D）。"
				: "Add your delivery address below to continue (tap \u201CAdd new address\u201D)."));
		const { balance } = storeToRefs(walletStore);
		const { availableCoupons } = storeToRefs(menuStore);

		const selectedOption = ref("delivery");
		const selectedDeliveryAddress = ref({});
		const selectedPickupAddress = ref({});

		// --- Seamless guest checkout: create the account, then the normal flow runs ---
		const guest = ref({ full_name: "", email: "", phone: "" });
		const guestLoading = ref(false);
		const guestError = ref("");
		const startGuestCheckout = async () => {
			guestError.value = "";
			const g = guest.value;
			if (!g.full_name.trim() || !g.email.trim() || !g.phone.trim()) {
				guestError.value = "Please enter your name, email and phone.";
				return;
			}
			guestLoading.value = true;
			try {
				const res = await caxios.post("/guest-checkout-start/", {
					full_name: g.full_name.trim(), email: g.email.trim(), phone: g.phone.trim(),
				});
				await authStore.applyGuestSession(res.data);
				try {
					await addressStore.getEligiblePostalCodes();
					await addressStore.fetchAddresses();
					await walletStore.fetchWallet();
				} catch (e) { /* non-blocking */ }
			} catch (e) {
				if (e.response?.status === 409) {
					guestError.value = "You’ve ordered with us before — please tap Login (top right) to sign in.";
				} else {
					guestError.value = e.response?.data?.detail || "Could not continue — please check your details.";
				}
			} finally {
				guestLoading.value = false;
			}
		};
		const showFail = ref(null);
		const showSuccess = ref(null);
		const router = useRouter();
		const route = useRoute();

		// Add save payment method state
		const savePaymentMethod = ref(false);
		const savedPaymentMethods = ref([]);
		const selectedSavedPaymentMethod = ref(null);

		if (totalQty.value <= 0) {
			router.push({
				name: "Ordernow",
			});
		}

		const stripe = ref(null);
		const elements = ref(null);
		const cardElement = ref(null);
		const cardElementRef = ref(null);
		const selectPaymentMethod = ref(null);
		const cardErrors = ref(null);
		const cardComplete = ref(false);

		const deliveryDatepicker = ref(null);
		const pickupDatepicker = ref(null);

		// Restaurant notes
		const notesTextField = ref("");
		const displayTextField = ref(false);

		const couponCode = ref("");
		const couponSuccess = ref(false);
		const couponError = ref(false);
		const couponErrorMessage = ref("");
		const couponLoading = ref(false);
		const isProcessingOrder = ref(false);
		// Refer-a-friend: a friend can type a code here (or it arrives via a ?ref link).
		const referralCode = ref("");
		const referralMsg = ref("");
		const referralOk = ref(false);
		const referralUnitDiscount = ref(0);   // $ the friend gets off, when the order qualifies
		const referralMinOrder = ref(0);       // $ subtotal the order must reach to qualify
		const checkReferral = async () => {
			const code = (referralCode.value || "").trim();
			referralMsg.value = ""; referralOk.value = false;
			referralUnitDiscount.value = 0; referralMinOrder.value = 0;
			if (!code) return;
			try {
				const subtotal_cents = Math.round((Number(TotalOrderPrice.value) || 0) * 100);
				const { data } = await caxios.post("/referrals/validate/", { code, subtotal_cents });
				referralOk.value = !!data.valid;
				referralUnitDiscount.value = Number(data.discount || data.friend_credit || 0);
				referralMinOrder.value = Number(data.min_order || 0);
				referralMsg.value = data.message || (data.valid ? "Referral applied." : "");
			} catch (e) {
				referralMsg.value = e.response?.data?.detail || "That referral code isn't valid.";
			}
		};
		// The live discount actually applied in the cart preview. The server recomputes
		// this authoritatively on submit — this only mirrors it so the friend sees it.
		const referralDiscount = computed(() => {
			// One offer per order (mirrors the server's no-stacking rule): a coupon
			// replaces the referral discount. Without this the checkout DISPLAYED both
			// discounts while the server charged only the coupon — the customer paid
			// more than the screen showed.
			if (cartStore.appliedCoupon) return 0;
			if (!referralOk.value || referralUnitDiscount.value <= 0) return 0;
			if ((Number(TotalOrderPrice.value) || 0) < referralMinOrder.value) return 0;
			return referralUnitDiscount.value;
		});
		// Re-check when the cart total crosses the minimum, so the message stays accurate.
		watch(() => TotalOrderPrice.value, () => { if ((referralCode.value || "").trim()) checkReferral(); });

		// Auto-select a delivery address. Without this the address renders UNSELECTED
		// (grey), the customer assumes it's chosen, clicks "Continue to payment" and hits
		// "Delivery Address is not selected" with no obvious cause — a silent checkout
		// blocker. Prefer the customer's primary address, else the first eligible one.
		watch(eligibleAddress, (list) => {
			if (!list || !list.length) return;
			const cur = selectedDeliveryAddress.value;
			const stillValid = cur && cur.id && list.some((a) => a.id === cur.id);
			if (!stillValid) selectedDeliveryAddress.value = list.find((a) => a.is_primary) || list[0];
		}, { immediate: true });
		// Same for pickup outlets, so switching to Pick Up isn't blocked either.
		watch(pickUpAddresses, (list) => {
			if (!list || !list.length) return;
			const cur = selectedPickupAddress.value;
			const stillValid = cur && cur.id && list.some((a) => a.id === cur.id);
			if (!stillValid) selectedPickupAddress.value = list[0];
		}, { immediate: true });

		// Duplicate-submit guard: a stable token per cart. Retries of the SAME cart reuse it
		// (the server returns the original order — no double charge); changing the cart mints
		// a new token (a genuinely new order). Cleared naturally when the cart empties.
		const genCheckoutKey = () => (window.crypto && window.crypto.randomUUID
			? window.crypto.randomUUID()
			: "ck-" + Date.now() + "-" + Math.random().toString(16).slice(2));
		const checkoutKey = ref(genCheckoutKey());
		watch(() => JSON.stringify(cart.value || []), () => { checkoutKey.value = genCheckoutKey(); });

		// Add window reference for tracking payment windows
		// const paymentWindow = ref(null);
		const originalTab = ref(true); // Track if this is the original tab
		const waitingForPaymentResult = ref(false); // Track if waiting for payment from another tab
		// Backend-controlled delivery-day rules (Operations → Delivery days).
		const orderConfig = ref({ open_weekdays: [1, 3, 5], cutoff_hour: 20, min_lead_days: 1, closed_dates: [], open_dates: [] });
		const fetchOrderingConfig = async () => {
			try {
				const res = await caxios.get("/menu/ordering-config/");
				if (res.data && Array.isArray(res.data.open_weekdays)) orderConfig.value = res.data;
			} catch (e) { /* keep defaults */ }
		};

		// Add cross-tab communication for WeChat payments
		const setupCrossTabCommunication = () => {
			// Listen for payment results from other tabs
			const handleStorageChange = (event) => {
				if (event.key === "wechat_payment_result") {
					const result = JSON.parse(event.newValue || "{}");
					console.log("Received WeChat payment result from another tab:", result);

					// Clear waiting state
					waitingForPaymentResult.value = false;

					if (result.paymentIntentId && result.redirectStatus) {
						// Process the payment result in the original tab
						processPaymentResult(result.paymentIntentId, result.redirectStatus, "wechat");

						// Clear the storage event
						localStorage.removeItem("wechat_payment_result");
					}
				}

				if (event.key === "alipay_payment_result") {
					const result = JSON.parse(event.newValue || "{}");
					console.log("Received Alipay payment result from another tab:", result);

					// Clear waiting state
					waitingForPaymentResult.value = false;

					if (result.paymentIntentId && result.redirectStatus) {
						// Process the payment result in the original tab
						processPaymentResult(result.paymentIntentId, result.redirectStatus, "alipay");

						// Clear the storage event
						localStorage.removeItem("alipay_payment_result");
					}
				}
			};

			window.addEventListener("storage", handleStorageChange);

			return () => {
				window.removeEventListener("storage", handleStorageChange);
			};
		};

		// Extract payment result processing logic
		const processPaymentResult = async (paymentIntentId, redirectStatus, paymentSource) => {
			console.log(`Processing payment result: ${redirectStatus} for ${paymentSource} payment`);
			isProcessingOrder.value = false; // Reset processing state

			// Restore form data first
			await restoreFormData();

			// Handle payment result with a slight delay to ensure DOM is ready
			setTimeout(() => {
				if (redirectStatus === "succeeded") {
					// Payment succeeded - clear saved form data
					clearSavedFormData();
					console.log("Payment succeeded, showing success modal");
					showSuccess.value?.click();
				} else {
					// Payment failed - keep form data for retry
					console.log("Payment failed, showing failure modal");
					console.log("Form data preserved for retry");
					showFail.value?.click();
				}
			}, 500);
		};

		const activeDatePickerRef = computed(() => {
			return selectedOption.value === "delivery" ? deliveryDatepicker : pickupDatepicker;
		});

		// The amount the customer actually pays for food (subtotal after every discount).
		const payableFood = computed(() =>
			Math.max(0, (Number(TotalOrderPrice.value) || 0) - (Number(TotalDiscount.value) || 0) - (Number(referralDiscount.value) || 0)));

		// 动态计算配送费 — applies the staff-set delivery policy (flat fee / zone
		// price / free over threshold). The server recomputes this authoritatively.
		const deliveryFee = computed(() => {
			if (selectedOption.value !== "delivery") return 0;
			// Top-up perk (mirrors the server rule): paying with store credit ships free
			// when the owner enables it. The serializer recomputes authoritatively.
			if (selectPaymentMethod.value === "wallet"
				&& addressStore.deliveryConfig.free_delivery_store_credit) return 0;
			return addressStore.quoteDeliveryFee({
				orderType: "DELIVERY",
				postalCode: selectedDeliveryAddress.value?.postal_code,
				payableFood: payableFood.value,
			});
		});

		// The real amount charged = food (after discount) + delivery. The wallet option is
		// gated on THIS (not the food subtotal), so it only enables when the wallet actually
		// covers what the server withdraws — otherwise the order failed with no explanation.
		const payableTotal = computed(() =>
			Math.max(0, payableFood.value + (Number(deliveryFee.value) || 0)));

		// How much more the customer must spend to unlock free delivery (0 = none / already free / disabled).
		const freeDeliveryRemaining = computed(() => {
			const threshold = Number(addressStore.deliveryConfig.free_delivery_threshold) || 0;
			if (threshold <= 0 || selectedOption.value !== "delivery") return 0;
			const remain = threshold - payableFood.value;
			return remain > 0 ? remain : 0;
		});

		// Minimum-order gate (0 = no minimum). Checked against the food subtotal.
		const minimumOrder = computed(() => Number(addressStore.deliveryConfig.minimum_order_amount) || 0);
		const belowMinimum = computed(() =>
			minimumOrder.value > 0 && (Number(TotalOrderPrice.value) || 0) < minimumOrder.value);

		const initializeStripe = async () => {
			try {
				stripe.value = await stripePromise;
				elements.value = stripe.value.elements();

				if (selectPaymentMethod.value === "stripe") {
					createAndMountCardElement();
				}
			} catch (error) {
				console.error("Error initializing Stripe:", error);
			}
		};

		const createAndMountCardElement = () => {
			// Don't create if card element already exists
			if (cardElement.value) return;

			// Don't create if elements is not initialized
			if (!elements.value) {
				console.error("Stripe elements not initialized");
				return;
			}

			cardElement.value = elements.value.create("card", {
				style: {
					base: {
						fontSize: "16px",
						color: "#32325d",
					},
				},
			});

			if (cardElementRef.value) {
				cardElement.value.mount(cardElementRef.value);
			} else {
				console.error("cardElementRef is null - DOM element not ready");
				return;
			}

			cardElement.value.on("change", (event) => {
				cardErrors.value = event.error ? event.error.message : "";
				cardComplete.value = event.complete;
			});
		};

		const unmountCardElement = () => {
			if (cardElement.value) {
				try {
					cardElement.value.destroy();
				} catch (error) {
					console.error("Error destroying card element:", error);
				}
				cardElement.value = null;
				cardErrors.value = null;
				cardComplete.value = false;
			}
		};

		const handleCheckOutSubmit = async () => {
			if (isProcessingOrder.value) return; // Prevent multiple submissions
			isProcessingOrder.value = true;
			// Minimum-order gate (server also enforces this). 0 = no minimum.
			if (belowMinimum.value) {
				alert(`The minimum order amount is A$${minimumOrder.value.toFixed(2)}. Please add a little more to your cart.`);
				isProcessingOrder.value = false;
				return;
			}
			const payload = {};
			if (!schedule_date.value) {
				alert("Select Schedule date");
				isProcessingOrder.value = false;
				return;
			}
			if (!schedule_time.value) {
				alert("Please select a time slot");
				isProcessingOrder.value = false;
				return;
			}
			if (selectedOption.value == "delivery") {
				if (Object.keys(selectedDeliveryAddress.value).length === 0) {
					alert("Delivery Address is not selected");
					isProcessingOrder.value = false;
					return;
				}
				payload.order_type = "DELIVERY";
				payload.delivery_location = selectedDeliveryAddress.value.id;
				payload.pickup_location = "";
			} else {
				if (Object.keys(selectedPickupAddress.value).length === 0) {
					alert("Pickup Address not selected");
					isProcessingOrder.value = false;
					return;
				}

				payload.order_type = "PICKUP";
				payload.delivery_location = "";
				payload.pickup_location = selectedPickupAddress.value.id;
			}
			payload.menu_item = JSON.stringify(cart.value);
			payload.payment_type = selectPaymentMethod.value;
			payload.schedule_date = schedule_date.value;
			payload.time_slot = schedule_time.value;
			payload.notes = notesTextField.value;
			payload.coupon = appliedCoupon.value?.code || null;

			// Add order_id if this is a re-payment from order list
			if (route.query.order_id) {
				payload.order_id = route.query.order_id;
			}

			const shippingCharges = deliveryFee.value;
			const refDisc = Number(referralDiscount.value) || 0;
			payload.idempotency_key = checkoutKey.value;   // duplicate-submit guard
			payload.tracking = checkoutTracking();          // _fbp/_fbc for the server-side Purchase (Conversions API)
			payload.amount = TotalOrderPrice.value.toFixed(2);
			payload.shipping_charges = shippingCharges.toFixed(2);
			// The server recomputes the authoritative discount (coupon + referral) itself;
			// we send our optimistic total so the two agree for a clean UX.
			payload.discount = (TotalDiscount.value + refDisc).toFixed(2);
			const finalPrice = TotalOrderPrice.value + shippingCharges - TotalDiscount.value - refDisc;

			// Validate final price is positive
			if (finalPrice <= 0) {
				alert("Total order amount must be greater than zero.");
				isProcessingOrder.value = false;
				return;
			}

			payload.total_price = finalPrice.toFixed(2);

			// Add save payment method flag
			payload.save_payment_method = savePaymentMethod.value;

			// CHDS Circle: attach the referral code (a typed code beats the ?ref cookie).
			// The server recomputes any discount itself — we only say WHICH code applied.
			try {
				const circle = useCircleStore();
				const typed = (referralCode.value || '').trim();
				if (typed) {
					payload.referral_code = typed;   // a friend's typed code wins
				} else if (circle.effectiveCode) {
					payload.referral_code = circle.effectiveCode;
					payload.referral_source = circle.source;
				}
			} catch (e) { /* a referral bug must never block an order */ }

			if (selectPaymentMethod.value === "wallet") {
				try {
					const response = await caxios.post("/orders/create/", payload);
					if (response.status === 201) {
						// First-party analytics purchase (links the funnel to the order)
						try { trackPurchase({ orderId: response.data?.order_id || payload.order_id, eventId: `purchase_${response.data?.order_id || payload.order_id}`, value: finalPrice, items: purchaseItems(), numItems: totalQty.value }); } catch (e) { /* noop */ }
						attachFindMyMealsOrder(response.data?.order_id || payload.order_id);
						showSuccess.value.click();
					} else {
						showFail.value.click();
					}
				} catch (error) {
					console.error("Wallet payment error:", error);
					console.error("Error details:", error.response?.data || error.message);

					// Show specific error message if available
					const couponErr = error.response?.data?.coupon;
					if (couponErr) {
						const msg = t("coupon_limit", ["checkout"]) || "This coupon has already been used.";
						cartStore.unapplyCoupon();
						couponError.value = true;
						couponErrorMessage.value = msg;
						alert("Order failed: " + msg);
					} else if (error.response?.data?.message) {
						alert("Order failed: " + error.response.data.message);
					} else if (error.code === "ECONNABORTED") {
						alert("Order timed out. Please check your connection and try again.");
					} else {
						alert("Order failed. Please try again.");
					}

					showFail.value.click();
				} finally {
					isProcessingOrder.value = false;
				}
			} else {
				try {
					const response = await caxios.post("/orders/create/", payload);
					const { client_secret } = response.data;
					// Persist the REAL order id (not the Stripe payment-intent id) so first-party
					// analytics can attribute the purchase to this order even after a redirect
					// (Alipay / WeChat / 3-D Secure land back on OrderSuccess, which reads these).
					const createdOrderId = response.data?.order_id || payload.order_id || "";
					attachFindMyMealsOrder(createdOrderId);
					if (typeof window !== "undefined") {
						localStorage.setItem("lastOrderId", createdOrderId);
						localStorage.setItem("lastOrderValue", finalPrice.toString());
						localStorage.setItem("lastOrderItems", totalQty.value.toString());
						try { localStorage.setItem("lastOrderLines", JSON.stringify(purchaseItems())); } catch (e) { /* noop */ }
					}

					let result;

					if (selectPaymentMethod.value === "stripe") {
						if (selectedSavedPaymentMethod.value) {
							// Use saved payment method
							result = await stripe.value.confirmCardPayment(client_secret, {
								payment_method: selectedSavedPaymentMethod.value,
							});
						} else {
							// Use new card
							result = await stripe.value.confirmCardPayment(client_secret, {
								payment_method: {
									card: cardElement.value,
									billing_details: {
										address: {
											line1: `${billing_object.street_address1},${billing_object.street_address2}, ${billing_object.suburbs}`,
											city: billing_object.city,
											country: "AU",
											postal_code: billing_object.postal_code,
										},
									},
								},
							});
						}
					} else if (selectPaymentMethod.value === "alipay") {
						// Save form data before redirect
						saveFormData();

						result = await stripe.value.confirmAlipayPayment(client_secret, {
							return_url: window.location.origin + "/checkout?source=alipay",
						});
					} else if (selectPaymentMethod.value === "wechat") {
						// Handle WeChat Payment
						console.log("Initiating WeChat payment with stripe instance:", stripe.value);

						// Save form data before redirect
						saveFormData();

						// Set waiting state for WeChat (likely to open new tab)
						waitingForPaymentResult.value = true;

						result = await stripe.value.confirmWechatPayPayment(client_secret, {
							payment_method_options: {
								wechat_pay: {
									client: "web",
								},
							},
							return_url: window.location.origin + "/checkout?source=wechat",
						});

						console.log("WeChat payment result:", result);

						// If we get here without redirect, clear waiting state
						setTimeout(() => {
							if (waitingForPaymentResult.value) {
								waitingForPaymentResult.value = false;
								console.log("WeChat payment completed without redirect");
							}
						}, 2000);
					}

					if (result.error) {
						// Show error to customer
						console.error("Payment error:", result.error.message);
						alert("Payment failed: " + result.error.message);
						showFail.value.click();
					} else {
						if (result.paymentIntent) {
							if (result.paymentIntent.status === "succeeded") {
								try { trackPurchase({ orderId: createdOrderId, eventId: `purchase_${createdOrderId}`, value: finalPrice, items: purchaseItems(), numItems: totalQty.value }); } catch (e) { /* noop */ }
								showSuccess.value.click();
							} else if (result.paymentIntent.status === "requires_action") {
								// Payment requires additional action (3D Secure, etc.)
								console.log("Payment requires additional action");
							} else if (result.paymentIntent.status === "processing") {
								try { trackPurchase({ orderId: createdOrderId, eventId: `purchase_${createdOrderId}`, value: finalPrice, items: purchaseItems(), numItems: totalQty.value }); } catch (e) { /* noop */ }
								console.log("Payment is processing");
								showSuccess.value.click();
							} else {
								console.log("Payment status:", result.paymentIntent.status);
							}
						} else {
							// For redirect-based payment methods (Alipay, WeChat),
							// store order value for tracking after redirect
							if (typeof window !== 'undefined') {
								localStorage.setItem('lastOrderValue', finalPrice.toString());
								localStorage.setItem('lastOrderItems', totalQty.value.toString());
								try { localStorage.setItem('lastOrderLines', JSON.stringify(purchaseItems())); } catch (e) { /* noop */ }
							}
							console.log("Redirect-based payment initiated");
						}
					}
				} catch (error) {
					console.error("Error during checkout:", error.response || error);

					const couponErr = error.response?.data?.coupon;
					let errorMessage;
					if (couponErr) {
						errorMessage = t("coupon_limit", ["checkout"]) || "This coupon has already been used.";
						cartStore.unapplyCoupon();
						couponError.value = true;
						couponErrorMessage.value = errorMessage;
					} else if (error.code === "ECONNABORTED") {
						errorMessage = "Order timed out. Please check your connection and try again.";
					} else if (error.response?.data?.detail) {
						errorMessage = "An error occurred during checkout: " + error.response.data.detail;
					} else if (error.response?.data?.message) {
						errorMessage = "An error occurred during checkout: " + error.response.data.message;
					} else {
						errorMessage = "An error occurred during checkout: " + error.message;
					}

					alert(errorMessage);
					showFail.value.click();
				} finally {
					isProcessingOrder.value = false;
				}
			}
		};

		// Handle pickup time slot selection change
		const handleTimeSlotChange = (event) => {
			const timeSlotId = event.target.value;
			workingDaysStore.setScheduleTime(timeSlotId);
		};

		const getCurrentDateInGMT11 = () => {
			// 获取当前时间的悉尼时区Date对象
			const now = new Date();

			// 获取悉尼时区的各个时间组成部分
			const sydneyOptions = {
				timeZone: "Australia/Sydney",
				year: "numeric",
				month: "numeric",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				second: "numeric",
				hour12: false,
			};

			const formatter = new Intl.DateTimeFormat("en-AU", sydneyOptions);
			const parts = formatter.formatToParts(now);

			// 从parts中提取各个值
			const dateComponents = {};
			parts.forEach((part) => {
				if (part.type !== "literal") {
					dateComponents[part.type] = parseInt(part.value);
				}
			});

			// 创建一个代表悉尼当前时间的Date对象
			// 注意：这个Date对象在本地时区中表示悉尼的日期和时间
			const sydneyDate = new Date(
				dateComponents.year,
				dateComponents.month - 1, // 月份从0开始
				dateComponents.day,
				dateComponents.hour,
				dateComponents.minute,
				dateComponents.second
			);

			return sydneyDate;
		};
		const calculateMinSelectableDate = () => {
			// Mirror the backend rule exactly (apps/orders/validators.py): a delivery date D is
			// orderable until `cutoffHour` on the day `leadDays` before D. So the earliest
			// "cut-off day" is TODAY if we're still before the cut-off hour, else TOMORROW; the
			// earliest delivery date is that cut-off day + leadDays, rolled to the next OPEN day.
			// (The previous heuristic used a fixed +1/+2-days jump that skipped valid near dates —
			// e.g. on a closed weekday it leapt past the very next open day.)
			const now = getCurrentDateInGMT11();
			const functionalDays = (orderConfig.value.open_weekdays && orderConfig.value.open_weekdays.length)
				? orderConfig.value.open_weekdays : [1, 3, 5];
			const cutoffHour = orderConfig.value.cutoff_hour ?? 20;
			const leadDays = Math.max(0, parseInt(orderConfig.value.min_lead_days ?? 1, 10) || 0);
			const closed = orderConfig.value.closed_dates || [];
			const openOverrides = orderConfig.value.open_dates || [];
			const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
			const isOpenDay = (d) => {
				const s = ymd(d);
				if (closed.includes(s)) return false;      // holiday override
				if (openOverrides.includes(s)) return true; // one-off open override
				return functionalDays.includes(d.getDay());
			};

			const cutoffDay = new Date(now);
			cutoffDay.setHours(0, 0, 0, 0);
			if (now.getHours() >= cutoffHour) {
				cutoffDay.setDate(cutoffDay.getDate() + 1); // today's cut-off has passed
			}
			const minDate = new Date(cutoffDay);
			minDate.setDate(minDate.getDate() + leadDays);
			let guard = 0;
			while (guard++ < 400 && !isOpenDay(minDate)) {
				minDate.setDate(minDate.getDate() + 1);
			}
			minDate.setHours(0, 0, 0, 0);
			return minDate;
		};

		const initializeFlatpickr = async () => {
			await nextTick(); // Wait for DOM update
			const el = activeDatePickerRef.value?.value;
			if (el) {
				flatpickr(el, {
					dateFormat: "Y-m-d",
					minDate: calculateMinSelectableDate(),
					enable: [
						function (date) {
							const cfg = orderConfig.value;
							const functionalDays = (cfg.open_weekdays && cfg.open_weekdays.length) ? cfg.open_weekdays : [1, 3, 5];
							const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
							if ((cfg.closed_dates || []).includes(ymd)) return false;   // holiday: force closed
							if ((cfg.open_dates || []).includes(ymd)) return true;      // one-off: force open
							return functionalDays.includes(date.getDay());
						},
					],
					onChange: (selectedDates, dateStr) => {
						console.log(selectedDates);
						console.log(dateStr);
						workingDaysStore.setScheduleDate(dateStr);
					},
				});
			}
		};

		const handleClear = () => {
			notesTextField.value = "";
		};

		const toggleNoteVisibility = () => {
			displayTextField.value = !displayTextField.value;
		};

		const openAddAddressModal = () => {
			addressStore.initializeActiveAddress();
		};

		// Add functions for saved payment methods
		const fetchSavedPaymentMethods = async () => {
			if (!authStore.accessToken) { savedPaymentMethods.value = []; return; }   // guests have no saved cards
			try {
				const response = await caxios.get("/stripe-payment-methods/");
				savedPaymentMethods.value = response.data;
			} catch (error) {
				console.error("Error fetching saved payment methods:", error);
			}
		};

		const handleUseSavedPaymentMethod = (paymentMethodId) => {
			selectedSavedPaymentMethod.value = paymentMethodId;
			// Hide the card element when using saved payment method
			if (cardElement.value) {
				unmountCardElement();
			}
		};

		const handleUseNewPaymentMethod = async () => {
			selectedSavedPaymentMethod.value = null;
			// Show the card element for new payment method
			if (selectPaymentMethod.value === "stripe") {
				await nextTick(); // Ensure DOM is updated
				createAndMountCardElement();
			}
		};

		// Save form data to localStorage before payment redirect
		const saveFormData = () => {
			const formData = {
				selectedOption: selectedOption.value,
				selectedDeliveryAddress: selectedDeliveryAddress.value,
				selectedPickupAddress: selectedPickupAddress.value,
				schedule_date: schedule_date.value,
				schedule_time: schedule_time.value,
				notesTextField: notesTextField.value,
				couponCode: couponCode.value,
				appliedCoupon: appliedCoupon.value,
				selectPaymentMethod: selectPaymentMethod.value,
				savePaymentMethod: savePaymentMethod.value,
				selectedSavedPaymentMethod: selectedSavedPaymentMethod.value,
			};

			try {
				localStorage.setItem("checkoutFormData", JSON.stringify(formData));
				console.log("Form data saved to localStorage");
			} catch (error) {
				console.error("Error saving form data:", error);
			}
		};

		// Restore form data from localStorage after payment redirect
		const restoreFormData = async () => {
			try {
				const savedData = localStorage.getItem("checkoutFormData");
				if (savedData) {
					const formData = JSON.parse(savedData);
					console.log("Restoring form data:", formData);

					// Wait for next tick to ensure DOM is ready
					await nextTick();

					// Restore basic form fields
					selectedOption.value = formData.selectedOption || "delivery";
					selectedDeliveryAddress.value = formData.selectedDeliveryAddress || {};
					selectedPickupAddress.value = formData.selectedPickupAddress || {};
					notesTextField.value = formData.notesTextField || "";
					selectPaymentMethod.value = formData.selectPaymentMethod || "stripe";
					savePaymentMethod.value = formData.savePaymentMethod || false;
					selectedSavedPaymentMethod.value = formData.selectedSavedPaymentMethod || null;

					// Restore working days store data first
					if (formData.schedule_date) {
						workingDaysStore.setScheduleDate(formData.schedule_date);

						// Update the flatpickr instance with the date
						setTimeout(() => {
							const activePickerElement = selectedOption.value === "delivery" ? deliveryDatepicker.value : pickupDatepicker.value;

							if (activePickerElement?._flatpickr) {
								activePickerElement._flatpickr.setDate(formData.schedule_date, false);
								console.log("Date picker updated with:", formData.schedule_date);
							}
						}, 100);
					}

					if (formData.schedule_time) {
						workingDaysStore.setScheduleTime(formData.schedule_time);

						// The template uses v-model="schedule_date" for time slots, which seems incorrect
						// but we need to work with it. Trigger change event to sync.
						setTimeout(() => {
							const timeSlotSelect = selectedOption.value === "delivery" ? document.getElementById("deliveryTimeSlot") : document.getElementById("pickupTimeSlot");

							if (timeSlotSelect) {
								// Find the option that matches our saved time
								const matchingOption = Array.from(timeSlotSelect.options).find((option) => option.value === formData.schedule_time);

								if (matchingOption) {
									timeSlotSelect.value = formData.schedule_time;
									// Trigger change event to ensure proper synchronization
									const event = new Event("change", { bubbles: true });
									timeSlotSelect.dispatchEvent(event);
									console.log("Time slot select updated with:", formData.schedule_time);
								}
							}
						}, 150);
					}

					// Restore coupon data
					if (formData.couponCode) {
						couponCode.value = formData.couponCode;

						// Update the coupon input field
						setTimeout(() => {
							const couponInput = document.getElementById("inputZip");
							if (couponInput) {
								couponInput.value = formData.couponCode;
								console.log("Coupon input field updated");
							}
						}, 100);
					}
					if (formData.appliedCoupon) {
						cartStore.applyCoupon(formData.appliedCoupon);
						// Update coupon UI state
						couponSuccess.value = true;
						couponError.value = false;
						couponErrorMessage.value = "";
					}

					// Update notes text area
					if (formData.notesTextField) {
						setTimeout(() => {
							const notesTextArea = document.querySelector('textarea[v-model="notesTextField"]');
							if (notesTextArea) {
								notesTextArea.value = formData.notesTextField;
								console.log("Notes text area updated");
							}
						}, 100);
					}

					// Force UI updates for address selections
					await nextTick();

					// Since the radio buttons use v-model, Vue's reactivity should handle the updates
					// Just ensure we have the correct data structure
					console.log("Address data restored:", {
						deliveryAddress: selectedDeliveryAddress.value,
						pickupAddress: selectedPickupAddress.value,
						option: selectedOption.value,
					});

					// Force reactivity update
					await nextTick();

					// Trigger watchers by temporarily changing and restoring values
					if (selectedOption.value) {
						const currentOption = selectedOption.value;
						selectedOption.value = currentOption === "delivery" ? "pickup" : "delivery";
						await nextTick();
						selectedOption.value = currentOption;
						console.log("Delivery/Pickup option reactivity triggered");
					}

					// Trigger payment method reactivity
					if (selectPaymentMethod.value) {
						const currentPayment = selectPaymentMethod.value;
						selectPaymentMethod.value = currentPayment === "stripe" ? "wallet" : "stripe";
						await nextTick();
						selectPaymentMethod.value = currentPayment;
						console.log("Payment method reactivity triggered");
					}

					// Re-initialize components that might need it
					setTimeout(async () => {
						await initializeFlatpickr();
						if (selectPaymentMethod.value === "stripe" && !selectedSavedPaymentMethod.value) {
							await initializeStripe();
						}
					}, 200);

					console.log("Form data restoration completed");
				}
			} catch (error) {
				console.error("Error restoring form data:", error);
			}
		};

		// Clear saved form data
		const clearSavedFormData = () => {
			try {
				localStorage.removeItem("checkoutFormData");
				console.log("Saved form data cleared");
			} catch (error) {
				console.error("Error clearing saved form data:", error);
			}
		};

		// Check for payment results when returning from redirect-based payments
		const checkPaymentResults = async () => {
			const paymentIntentId = route.query.payment_intent;
			const redirectStatus = route.query.redirect_status;
			const paymentSource = route.query.source; // 'alipay' or 'wechat'

			console.log("Checking payment results:", {
				paymentIntentId,
				redirectStatus,
				paymentSource,
				allQueryParams: route.query,
				hasOpener: !!window.opener,
				isNewTab: !!(paymentIntentId && redirectStatus && paymentSource),
			});

			// If we have payment parameters, we're returning from a payment
			if (paymentIntentId && redirectStatus && paymentSource) {
				console.log(`Payment result: ${redirectStatus} for ${paymentSource} payment`);

				// Check if this is a new tab/window (especially for WeChat)
				if (window.opener && !window.opener.closed) {
					console.log("This appears to be a new tab opened by payment provider");

					// Send payment result to the original tab via localStorage
					const paymentResult = {
						paymentIntentId,
						redirectStatus,
						paymentSource,
						timestamp: Date.now(),
					};

					try {
						localStorage.setItem(`${paymentSource}_payment_result`, JSON.stringify(paymentResult));
						console.log(`Payment result sent to original tab via localStorage for ${paymentSource}`);

						// Close this tab after a brief delay
						setTimeout(() => {
							window.close();
						}, 1000);

						// If window.close() doesn't work, redirect to a closing page
						setTimeout(() => {
							if (!window.closed) {
								document.body.innerHTML = `
                                    <div style="text-align: center; padding: 50px; font-family: Arial, sans-serif;">
                                        <h2>Payment ${redirectStatus === "succeeded" ? "Successful" : "Failed"}</h2>
                                        <p>You can close this tab and return to the original checkout page.</p>
                                        <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px;">Close Tab</button>
                                    </div>
                                `;
							}
						}, 2000);
					} catch (error) {
						console.error("Error sending payment result to original tab:", error);
					}

					return; // Don't process further in this tab
				}

				// If this is the same tab (like with Alipay), process normally
				originalTab.value = true;
				await processPaymentResult(paymentIntentId, redirectStatus, paymentSource);

				// Clear URL parameters to prevent re-processing on refresh
				const newUrl = window.location.pathname;
				window.history.replaceState({}, document.title, newUrl);
			} else if (paymentIntentId || redirectStatus) {
				// Partial parameters - could be an issue
				console.warn("Incomplete payment parameters detected:", {
					paymentIntentId,
					redirectStatus,
					paymentSource,
					allParams: route.query,
				});

				// Still restore form data in case of partial return
				await restoreFormData();
			}
		};

		// Add beforeunload handler to warn about leaving during payment
		const handleBeforeUnload = (event) => {
			if (isProcessingOrder.value) {
				const message = "Your payment is being processed. Are you sure you want to leave?";
				event.returnValue = message;
				return message;
			}
		};

		// Cart line editing — qty steppers + remove (discount auto-recomputes via updateTotal).
		const cartIncrease = (id) => cartStore.increaseItemQuantity(id);
		const cartDecrease = (id) => cartStore.decreaseItemQuantity(id);
		const cartRemove = (id) => cartStore.removeFromCart(id);

		const handleCouponApply = (coupon) => {
			cartStore.applyCoupon(coupon);
		};

		const handleCouponUnapply = () => {
			cartStore.unapplyCoupon();
			couponCode.value = "";
			couponSuccess.value = false;
			couponError.value = false;
			couponErrorMessage.value = "";
		};

		const validateAndApplyCoupon = async () => {
			couponLoading.value = true;
			couponError.value = false;
			couponSuccess.value = false;
			couponErrorMessage.value = "";

			const enteredCode = couponCode.value.trim();

			if (!enteredCode) {
				couponError.value = false;
				couponErrorMessage.value = "";
				couponLoading.value = false;
				return;
			}

			try {
				// Call backend API to validate coupon
				const response = await caxios.post("/transactions/coupons/orders/validate/", {
					code: enteredCode,
				});

				if (response.data.valid) {
					// Coupon is valid, apply it
					handleCouponApply(response.data.coupon);
					couponSuccess.value = true;
				} else {
					// Coupon is invalid or already used
					couponError.value = true;
					// Translate the error message code from backend
					couponErrorMessage.value = t(response.data.message, ["checkout"]) || "Invalid coupon code.";
				}
			} catch (error) {
				couponError.value = true;
				// Translate the error message code from backend
				couponErrorMessage.value = t(error.response?.data?.message, ["checkout"]) || "Error validating coupon. Please try again.";
			}

			couponLoading.value = false;
		};

		watch(totalQty, (newCount, oldCount) => {
			if (newCount <= 0 && newCount !== oldCount) {
				router.push({
					name: "Ordernow",
				});
			}
		});

		watch(selectPaymentMethod, async (newValue, oldValue) => {
			console.log("Moving from", oldValue, "to", newValue);

			// Unmount card element when switching away from stripe
			if (oldValue === "stripe" && cardElement.value) {
				unmountCardElement();
			}

			if (newValue === "wallet") {
				// No Stripe initialization needed for wallet
				return;
			} else {
				// Initialize Stripe for all non-wallet payment methods
				await initializeStripe();
			}
		});

		watch(selectedOption, async (newVal) => {
			await nextTick();
			if (newVal === "delivery") {
				initializeFlatpickr(deliveryDatepicker);
			} else {
				initializeFlatpickr(pickupDatepicker);
			}
		});

		watch(couponCode, (newVal) => {
			if (newVal === "") {
				couponError.value = "";
			}
		});

		watch(appliedCoupon, (newVal) => {
			if (newVal) {
				couponCode.value = newVal.code;
				couponSuccess.value = true;
				couponError.value = false;
				couponErrorMessage.value = "";
			} else {
				couponCode.value = "";
				couponSuccess.value = false;
				couponError.value = false;
				couponErrorMessage.value = "";
			}
		});

		onMounted(async () => {
			await nextTick();
			workingDaysStore.fetchTimeSlots();
			addressStore.fetchDeliveryConfig();  // load delivery pricing policy (fee / free threshold / min)
			await fetchOrderingConfig();  // load delivery-day rules before the date picker

			// Ensure coupons are loaded
			if (!availableCoupons.value || availableCoupons.value.length === 0) {
				await menuStore.fetchCoupons();
			}

			// Check if there's already a coupon applied in the cart
			if (appliedCoupon.value) {
				couponCode.value = appliedCoupon.value.code;
				couponSuccess.value = true;
				couponError.value = false;
				couponErrorMessage.value = "";
			}

			if (payableTotal.value > Number(balance.value) || !balance.value) {
				selectPaymentMethod.value = "stripe";
				await initializeStripe();
			} else {
				selectPaymentMethod.value = "wallet";
			}
			await initializeFlatpickr();

			// Fetch saved payment methods
			await fetchSavedPaymentMethods();
			await checkPaymentResults(); // Check for payment results on mount

			// Restore form data if available (for page refreshes or direct navigation)
			if (!route.query.payment_intent && !route.query.redirect_status) {
				await restoreFormData();
			}

			// Add beforeunload handler
			window.addEventListener("beforeunload", handleBeforeUnload);

			// Set up cross-tab communication and store cleanup function
			const cleanupCrossTab = setupCrossTabCommunication();

			// Store cleanup function for later use
			window.cleanupCrossTabCommunication = cleanupCrossTab;
		});

		onUpdated(async () => {
			await initializeFlatpickr();
		});

		onBeforeUnmount(() => {
			// Clear coupon and reset discount when leaving checkout page
			cartStore.unapplyCoupon();
			window.removeEventListener("beforeunload", handleBeforeUnload);

			// Clean up cross-tab communication
			if (window.cleanupCrossTabCommunication) {
				window.cleanupCrossTabCommunication();
				delete window.cleanupCrossTabCommunication;
			}

			// Clear saved form data when leaving checkout (except for payment redirects)
			if (!isProcessingOrder.value) {
				clearSavedFormData();
			}
		});

		return {
			t,
			user,
			guest,
			guestLoading,
			guestError,
			startGuestCheckout,
			cart,
			totalQty,
			cardComplete,
			billing_address,
			eligibleAddress,
			hasAnyAddress,
			firstAddressHint,
			pickUpAddresses,
			TotalOrderPrice,
			payableTotal,
			TotalDiscount,
			cartIncrease,
			cartDecrease,
			cartRemove,
			selectedOption,
			deliveryDatepicker,
			pickupDatepicker,
			showFail,
			balance,
			showSuccess,
			selectPaymentMethod,
			selectedDeliveryAddress,
			selectedPickupAddress,
			handleCheckOutSubmit,
			cardElementRef,
			handleTimeSlotChange,
			delivery_time_slots,
			pickup_time_slots,
			slotOptionLabel,
			displayTextField,
			notesTextField,
			handleClear,
			toggleNoteVisibility,
			openAddAddressModal,
			availableCoupons,
			handleCouponApply,
			appliedCoupon,
			handleCouponUnapply,
			couponCode,
			referralCode,
			referralMsg,
			referralOk,
			referralDiscount,
			checkReferral,
			couponSuccess,
			couponError,
			couponErrorMessage,
			couponLoading,
			validateAndApplyCoupon,
			isProcessingOrder,
			// Add new saved payment method variables
			savePaymentMethod,
			savedPaymentMethods,
			selectedSavedPaymentMethod,
			handleUseSavedPaymentMethod,
			handleUseNewPaymentMethod,
			waitingForPaymentResult,
			deliveryFee, // 添加配送费
			freeDeliveryRemaining,
			belowMinimum,
			minimumOrder,
		};
	},
};
</script>

<template>
	<div class="container">
		<!-- Guest gate: seamless account creation for first-time customers -->
		<div v-if="!user" class="guest-gate">
			<div class="gg-card">
				<h3 class="gg-title">Almost there — just your details</h3>
				<p class="gg-sub">First time ordering? We’ll set up your account as you order — no password needed. Already a customer? Tap <b>Login</b> at the top.</p>
				<input v-model="guest.full_name" class="gg-in" placeholder="Full name" />
				<input v-model="guest.email" class="gg-in" type="email" placeholder="Email address" />
				<input v-model="guest.phone" class="gg-in" type="tel" placeholder="Phone number" />
				<div v-if="guestError" class="gg-err">{{ guestError }}</div>
				<button class="gg-btn" :disabled="guestLoading" @click="startGuestCheckout">
					{{ guestLoading ? 'Setting up…' : 'Continue to delivery & payment →' }}
				</button>
				<p class="gg-fine">🔒 Your details are only used for your order &amp; delivery.</p>
			</div>
		</div>

		<div v-else class="row">
			<div class="col-lg-7 col-md-12 col-sm-12 col-12 p-3">
				<div class="store-n-diliveri bg-white p-2 rounded mb-3">
					<div class="outlet-with-billing-add">
						<div class="checkout-title-card">
							<router-link to="/ordernow" class="back-button text-decoration-none">
								<i class="fa-regular fa-circle-left"></i>
							</router-link>
							<div class="outlet-card p-2">
								<div class="outlet-detail">
									<h5>{{ t("chi_hun_da_su", ["checkout"]) }}</h5>
									<p>{{ t("location", ["checkout"]) }}</p>
								</div>
							</div>
						</div>
						<div class="billing-addshown" v-if="billing_address">
							<p>{{ t("billing_address", ["checkout"]) }}</p>
							<h6>{{ billing_address }}</h6>
						</div>
					</div>

					<div class="p-2">
						<div class="order-type-container">
							<div class="delivery-option rounded w-100">
								<!-- Delivery Option -->
								<input type="radio" class="btn-check" name="optionsdelivery" id="deliverychosen" autocomplete="off" value="delivery" v-model="selectedOption" />
								<label class="btn btn-primary" for="deliverychosen">
									<span> {{ t("delivery", ["checkout"]) }} </span>
								</label>

								<!-- Pickup Option -->
								<input type="radio" class="btn-check" name="optionsdelivery" id="pickupchosen" autocomplete="off" value="pickup" v-model="selectedOption" />
								<label class="btn btn-outline-primary" for="pickupchosen">
									{{ t("pick_up", ["checkout"]) }}
								</label>
							</div>
						</div>
						<div class="selected-address-container mt-3 p-2" v-if="selectedOption == 'delivery'">
							<div class="alert alert-danger" role="alert" v-if="!delivery_time_slots.length">Kitchen is not taking any delivery orders !!</div>
							<div class="schedule-order mb-3" v-else>
								<h6>{{ t("select_delivery_date_and_time", ["checkout"]) }}</h6>
								<div class="row">
									<!-- Select Delivery Day -->
									<div class="col-lg-6 col-md-6 col-sm-12 col-12 p-2">
										<label for="delivery-datepicker" class="form-label">{{ t("choose_a_date", ["checkout"]) }}</label>
										<input type="text" ref="deliveryDatepicker" id="delivery-datepicker" class="form-control datepicker" @load="initializeFlatpickr" />
									</div>

									<div class="col-lg-6 col-md-6 col-sm-12 col-12 p-2">
										<label for="deliveryTimeSlot" class="form-label">{{ t("choose_time_slot", ["checkout"]) }}</label>
										<select id="deliveryTimeSlot" class="form-select" v-model="schedule_time" @change="handleTimeSlotChange">
											<option disabled value="">
												{{ delivery_time_slots.length ? t("choose_time_slot", ["checkout"]) : t("no_time_slots", ["checkout"]) }}
											</option>
											<option v-for="slot in delivery_time_slots" :key="slot.id" :value="slot.name">
												{{ slotOptionLabel(slot) }}
											</option>
										</select>
									</div>
								</div>
							</div>
							<div class="alert alert-danger" role="alert" v-if="!eligibleAddress.length">
								<template v-if="!hasAnyAddress">{{ firstAddressHint }}</template>
								<template v-else>{{ t("eligible _delivery_address_warning", ["checkout"]) }}</template>
							</div>
							<div class="address-radio">
								<div v-for="address in eligibleAddress" :key="address.id">
									<input type="radio" class="btn-check" name="deliveryAddress" :id="'delivery-' + address.id" autocomplete="off" v-model="selectedDeliveryAddress" :value="address" />
									<label class="btn btn-primary" :for="'delivery-' + address.id">
										<p class="address-name mb-0">{{ address.name }}</p>
										<p class="address-description mb-0">{{ address.street_address1 }}, {{ address.street_address2 }}, {{ address.suburbs }}, {{ address.postal_code }}</p>
									</label>
								</div>

								<a href="" class="text-decoration-none" data-bs-toggle="modal" data-bs-target="#addressModal" @click="openAddAddressModal">
									<div class="add-addrss-button">
										<div>
											<i class="fa-solid fa-plus"></i>
										</div>
										<p class="mb-0">{{ t("add_new_address", ["checkout"]) }}</p>
									</div>
								</a>
							</div>
						</div>

						<div class="pickup-details-container mt-3 p-2" v-else>
							<div class="alert alert-danger" role="alert" v-if="!pickup_time_slots.length">Kitchen is closed for Pickup order.</div>
							<div class="schedule-order mb-3" v-else>
								<h6>{{ t("select_pickup_date_and_time", ["checkout"]) }}</h6>
								<div class="row">
									<!-- Select Pickup Day -->
									<div class="col-lg-6 col-md-6 col-sm-12 col-12 p-2">
										<label for="pickup-datepicker" class="form-label" @click="initializeFlatpickr">Choose a date</label>
										<input type="text" ref="pickupDatepicker" id="pickup-datepicker" class="form-control datepicker" />
									</div>

									<!-- Select Pickup Time Slot -->
									<div class="col-lg-6 col-md-6 col-sm-12 col-12 p-2">
										<label for="pickupTimeSlot" class="form-label">{{ t("choose_time_slot", ["checkout"]) }}</label>
										<select id="pickupTimeSlot" class="form-select" v-model="schedule_time" @change="handleTimeSlotChange">
											<option disabled value="">
												{{ pickup_time_slots.length ? t("choose_time_slot", ["checkout"]) : t("no_time_slots", ["checkout"]) }}
											</option>
											<option v-for="slot in pickup_time_slots" :key="slot.id" :value="slot.name">
												{{ slotOptionLabel(slot) }}
											</option>
										</select>
									</div>
								</div>
							</div>
							<div>
								<h6>{{ t("select_the_outlet", ["checkout"]) }}</h6>
								<div class="address-radio">
									<div v-for="address in pickUpAddresses" :key="address.id">
										<input type="radio" class="btn-check" name="outlets" :id="'pickup-' + address.id" autocomplete="off" v-model="selectedPickupAddress" :value="address" />
										<label class="btn btn-primary" :for="'pickup-' + address.id">
											<p class="address-name mb-0">{{ address.name }}</p>
											<p class="address-description mb-0">{{ address.street_address1 }},{{ address.street_address2 }},</p>
											<p class="address-description mb-0">{{ address.city }}, {{ address.state }},</p>
											<p class="address-description mb-0">{{ address.postal_code }}, Australia</p>
										</label>
									</div>
								</div>
							</div>
						</div>
						<div class="coupon-code-apply p-2">
							<label for="refInput" class="form-label">Referral code (Optional)</label>
							<div class="input-group">
								<input type="text" class="form-control" placeholder="A friend's code" id="refInput" v-model="referralCode" @blur="checkReferral" @keyup.enter="checkReferral" />
								<button class="btn btn-outline-primary" type="button" @click="checkReferral">{{ t("apply", ["checkout"]) }}</button>
							</div>
							<div v-if="referralMsg" class="mt-2 alert" :class="referralOk ? 'alert-success' : 'alert-danger'">{{ referralMsg }}</div>
						</div>
						<div class="coupon-code-apply p-2">
							<label for="inputZip" class="form-label">Coupon Code (Optional)</label>
							<div class="input-group">
								<input type="text" class="form-control" placeholder="Enter Coupon Code" id="inputZip" v-model="couponCode" @keyup.enter="validateAndApplyCoupon" :disabled="couponLoading || couponSuccess" />
								<button class="btn btn-outline-primary" type="button" @click="validateAndApplyCoupon" :disabled="couponLoading || couponSuccess">
									<span v-if="couponLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
									<span v-else>{{ t("apply", ["checkout"]) }}</span>
								</button>
							</div>
							<div v-if="couponSuccess && !couponLoading" class="mt-2 alert alert-success d-flex justify-content-between align-items-center">
								Coupon applied successfully!
								<button type="button" class="btn btn-sm btn-danger" @click="handleCouponUnapply">Remove</button>
							</div>
							<!-- Error Message -->
							<div v-if="couponError && !couponLoading" class="mt-2 alert alert-danger">
								{{ couponErrorMessage }}
							</div>
						</div>
					</div>
				</div>
				<!-- items added in the cart list -->
				<h6>{{ t("items_added", ["checkout"]) }}</h6>
				<div class="store-n-diliveri bg-white p-3 rounded mb-3">
					<ul class="list-unstyled mb-0">
						<!-- item for checkout -->
						<li class="Item-for-checkout" v-for="item in cart" :key="item.id">
							<div>
								<p class="mb-0 ci-name">{{ item.meal_trans_code ? t(item.meal_trans_code, ['menu_item']) : item.meal_name }}</p>
								<p class="item-type-hd mb-0">{{ item.selected_meal_portion_name }} ({{ item.selected_meal_portion_weight }}g)</p>
								<p class="item-type-hd mb-0" v-for="addon in item.addons" :key="addon.id">Extra {{ addon.name }}</p>
								<div class="ci-qty">
									<button type="button" class="ci-step" @click="cartDecrease(item.id)" aria-label="Decrease quantity">−</button>
									<span class="ci-count">{{ item.quantity }}</span>
									<button type="button" class="ci-step" @click="cartIncrease(item.id)" aria-label="Increase quantity">+</button>
									<button type="button" class="ci-remove" @click="cartRemove(item.id)" aria-label="Remove item"><i class="fa-solid fa-trash-can"></i></button>
								</div>
							</div>
							<div class="price-n-item-option">
								<p class="mb-0">A$ {{ Number(item.total_price).toFixed(2) }}</p>
							</div>
						</li>
					</ul>
					<div id="app-note">
						<!-- Button with pencil icon to toggle visibility -->
						<button @click="toggleNoteVisibility" v-if="!notesTextField && !displayTextField" class="btn btn-secondary"><i class="fa fa-pencil"></i> {{ t("add_notes_restaurant", ["checkout"]) }}</button>

						<p v-if="notesTextField && !displayTextField">
							{{ notesTextField }} <span @click="toggleNoteVisibility"><i class="fa fa-pencil"></i></span>
						</p>

						<!-- The note section, initially hidden -->
						<div id="app-restaurant" v-if="displayTextField">
							<textarea placeholder="Write your note here..." v-model="notesTextField"></textarea>
							<div class="order-type-container">
								<div class="delivery-option rounded w-100">
									<input type="radio" class="btn-check" name="notesAction" id="Clear" autocomplete="off" @click="handleClear" checked value="clear" />
									<label class="btn btn-primary" for="Clear"
										><span> {{ t("clear", ["checkout"]) }} </span></label
									>
									<input type="radio" class="btn-check" name="notesAction" id="Save" autocomplete="off" @click="toggleNoteVisibility" value="save" />
									<label class="btn btn-outline-primary" for="Save"> {{ t("save", ["checkout"]) }} </label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<!-- can be removed -->
			<div class="col-lg-5 col-md-12 col-sm-12 col-12 p-3">
				<div class="store-n-diliveri bg-white p-2 rounded">
					<!-- <h6 class="extra-status" v-if="availableCoupons.length >0">{{ t('apply_coupons', ['checkout']) }}</h6>
                <div class="store-n-diliveri bg-white pb-3 rounded" v-if="availableCoupons.length >0">
                    <div class="row">
                        <div 
                            class="m-2"
                            v-for="coupon in availableCoupons" 
                            :key="coupon.id"
                        >
                            <div class="card shadow-sm border-dark p-2">
                                <div class=" d-flex">
                                    <div class="flex-fill">
                                        <h5 class="card-title" style="color: #944C22;">{{ coupon.code }}</h5>
                                        <p class="card-text" style="color: #C5886B;">{{ coupon.description }}</p>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <button 
                                            class="btn btn-primary flex-fill"
                                            @click="handleCouponApply(coupon)"
                                            :disabled="appliedCoupon && appliedCoupon.id === coupon.id"
                                        >
                                            {{ appliedCoupon && appliedCoupon.id === coupon.id 
                                                ? t('applied', ['checkout']) 
                                                : t('apply', ['checkout']) }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> 
                </div> -->

					<!-- <div class="applied-coupon-section mt-4" v-if="appliedCoupon">
                    <div class="alert alert-success d-flex justify-content-between align-items-center" role="alert">
                        <div>
                        <strong>{{ t('applied_coupon', ['checkout']) }}:</strong> {{ appliedCoupon.code }}
                        
                        <span>
                            {{ t('you_would_get_cash_back_of', ['checkout']) }} A${{ TotalDiscount.toFixed(2) }}
                        </span>
                        </div>
                        <a href="#" @click.prevent="handleCouponUnapply" class="btn btn-sm btn-outline-danger">
                        {{ t('unapply', ['checkout']) }}
                        </a>
                    </div>
                </div> -->

					<!-- End of Coupons Section -->

					<!-- Other Checkout Content -->
				</div>

				<!-- Addons Section -->
				<div class="p-3 bg-white rounded">
					<h4 class="extra-status mb-3">{{ t("select_payment_method", ["checkout"]) }}</h4>

					<!-- Payment Timeout Warning -->
					<div class="alert alert-success mb-3" role="alert">
						<i class="fa fa-clock me-2"></i>
						<small>{{ t("payment_timeout_warning", ["checkout"]) }}</small>
					</div>

					<div class="meal-addons payment-method rounded">
						<!-- Wallet Option -->
						<input type="radio" class="btn-check" name="options" id="addons1" value="wallet" v-model="selectPaymentMethod" checked />
						<label class="btn btn-primary w-100" for="addons1">
							<p class="mb-0">
								<img class="payment-icon" src="@/assets/wallet.png" alt="Wallet" />
							</p>
						</label>

						<!-- Stripe Option -->
						<input type="radio" class="btn-check" name="options" id="addons2" value="stripe" v-model="selectPaymentMethod" />
						<label class="btn btn-primary w-100" for="addons2">
							<p class="mb-0">
								<img class="payment-icon" src="@/assets/card.jpg" alt="Stripe" />
							</p>
						</label>

						<!-- WeChat Option -->
						<input type="radio" class="btn-check" name="options" id="addons3" value="wechat" v-model="selectPaymentMethod" />
						<label class="btn btn-primary w-100" for="addons3">
							<p class="mb-0">
								<img class="payment-icon" src="@/assets/wechat.png" alt="WeChat" />
							</p>
						</label>

						<!-- PayPal Option -->
						<span data-bs-toggle="tooltip" data-bs-placement="bottom" title="Service unavailable">
							<input type="radio" class="btn-check" name="options" id="addons4" value="paypal" disabled />
							<label class="btn btn-primary w-100" for="addons4">
								<p class="mb-0">
									<img class="payment-icon" src="@/assets/money.png" alt="PayPal" />
								</p>
							</label>
						</span>

						<!-- Alipay Option -->
						<input type="radio" class="btn-check" name="options" id="addons5" value="alipay" v-model="selectPaymentMethod" />
						<label class="btn btn-primary w-100" for="addons5">
							<p class="mb-0">
								<img class="payment-icon" src="@/assets/logo-alipay.png" alt="Alipay" />
							</p>
						</label>
					</div>
				</div>

				<!-- Billing Details Section -->
				<div class="billing-details-container bg-white p-3 rounded" style="margin-top: 1rem">
					<div class="billing-detail detail-green">
						<p>{{ t("subtotal", ["checkout"]) }}</p>
						<p>A$ {{ Number(TotalOrderPrice).toFixed(2) }}</p>
					</div>

					<div class="billing-detail discount-line" v-if="appliedCoupon && TotalDiscount > 0">
						<p>{{ t("discount", ["checkout"]) || "Discount" }} <span class="coupon-tag">{{ appliedCoupon.code }}</span></p>
						<p class="disc-amt">− A$ {{ TotalDiscount.toFixed(2) }}</p>
					</div>

					<div class="billing-detail discount-line" v-if="referralDiscount > 0">
						<p>{{ t("referral_discount", ["checkout"], "Referral discount") }} <span class="coupon-tag">{{ (referralCode || '').trim().toUpperCase() }}</span></p>
						<p class="disc-amt">− A$ {{ referralDiscount.toFixed(2) }}</p>
					</div>

					<!-- Both a coupon AND a valid referral entered: say plainly which one counts. -->
					<div class="stack-note" v-if="appliedCoupon && referralOk && referralUnitDiscount > 0">
						{{ t("one_offer_per_order", ["checkout"], "One offer per order — the coupon has been applied; the referral discount doesn't combine with it.") }}
					</div>

					<div class="billing-detail detail-green" v-if="selectedOption === 'delivery'">
						<p class="">{{ t("delivery", ["checkout"]) || "Delivery" }}</p>
						<p class="" :class="{ 'free-ship': deliveryFee === 0 }">{{ deliveryFee === 0 ? (t("free", ["checkout"]) || "FREE") : "A$ " + deliveryFee.toFixed(2) }}</p>
					</div>

					<div class="free-ship-nudge" v-if="selectedOption === 'delivery' && freeDeliveryRemaining > 0">
							🚚 {{ (t("spend_more_free_delivery", ["checkout"]) || "Add A${amt} more for free delivery").replace("{amt}", freeDeliveryRemaining.toFixed(2)) }}
						</div>

						<div class="billing-detail grand-total order-total-line">
						<p>{{ t("total", ["checkout"]) || "Total" }}</p>
						<p>A$ {{ (Number(TotalOrderPrice) + (selectedOption === 'delivery' ? deliveryFee : 0) - TotalDiscount - referralDiscount).toFixed(2) }}</p>
					</div>

					<!-- Display balances for specific payment methods -->
					<div class="billing-detail detail-green my-2" v-if="selectPaymentMethod === 'wallet'">
						<p>{{ t("wallet_balance", ["checkout"]) }}</p>
						<p>A$ {{ balance }}</p>
					</div>
					<div v-if="selectPaymentMethod === 'wallet' && Number(balance) < payableTotal" style="font-size:12.5px;color:#a3521b;background:#fdf3e6;border:1px solid #f0d6ad;border-radius:8px;padding:8px 10px;margin-bottom:8px;">
						Your wallet balance (A$ {{ Number(balance).toFixed(2) }}) doesn't cover this order (A$ {{ payableTotal.toFixed(2) }}). Please choose card payment, or top up your wallet.
					</div>

					<!-- Credit Card Option -->
					<div v-if="selectPaymentMethod === 'stripe'" class="mt-4">
						<!-- Saved Payment Methods -->
						<div v-if="savedPaymentMethods.length > 0" class="saved-payment-methods mb-4">
							<h6>{{ t("saved_payment_methods", ["checkout"]) || "Saved Payment Methods" }}</h6>
							<div class="saved-cards">
								<div v-for="paymentMethod in savedPaymentMethods" :key="paymentMethod.id" class="saved-card" :class="{ active: selectedSavedPaymentMethod === paymentMethod.stripe_payment_method_id }" @click="handleUseSavedPaymentMethod(paymentMethod.stripe_payment_method_id)">
									<div class="card-info">
										<span class="card-brand">{{ paymentMethod.card_brand.toUpperCase() }}</span>
										<span class="card-last4">•••• {{ paymentMethod.card_last4 }}</span>
										<span class="card-expiry">{{ paymentMethod.card_exp_month }}/{{ paymentMethod.card_exp_year }}</span>
									</div>
									<div class="card-actions">
										<span v-if="paymentMethod.is_default" class="default-badge">Default</span>
									</div>
								</div>
							</div>

							<div class="mt-3">
								<button type="button" class="btn btn-outline-primary btn-sm" @click="handleUseNewPaymentMethod">
									{{ t("use_new_card", ["checkout"]) || "Use New Card" }}
								</button>
							</div>
						</div>

						<!-- New Card Form -->
						<div v-if="!selectedSavedPaymentMethod" class="new-card-form">
							<h6>{{ savedPaymentMethods.length > 0 ? t("add_new_card", ["checkout"]) || "Add New Card" : t("enter_card_details", ["checkout"]) || "Enter Card Details" }}:</h6>
							<div id="card-element" ref="cardElementRef"></div>

							<!-- Save Payment Method Checkbox -->
							<div class="form-check mt-3">
								<input type="checkbox" class="form-check-input" id="savePaymentMethod" v-model="savePaymentMethod" />
								<label class="form-check-label" for="savePaymentMethod">
									{{ t("save_payment_method", ["checkout"]) || "Save payment method for future use" }}
								</label>
							</div>

							<button type="button" class="btn btn-primary w-100 mt-2" @click="handleCheckOutSubmit" :disabled="!cardComplete || isProcessingOrder">
								<span v-if="isProcessingOrder" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
								{{ isProcessingOrder ? t("processing", ["checkout"]) || "Processing..." : t("continue_payment", ["checkout"]) }}
							</button>
						</div>

						<!-- Use Saved Payment Method -->
						<div v-if="selectedSavedPaymentMethod" class="use-saved-payment">
							<button type="button" class="btn btn-primary w-100 mt-2" @click="handleCheckOutSubmit" :disabled="isProcessingOrder">
								<span v-if="isProcessingOrder" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
								{{ isProcessingOrder ? t("processing", ["checkout"]) || "Processing..." : t("continue_payment", ["checkout"]) }}
							</button>
						</div>
					</div>

					<!-- Wallet Payment Option -->
					<button type="button" class="btn btn-primary w-100 mt-2" v-else-if="selectPaymentMethod === 'wallet'" @click="handleCheckOutSubmit" :disabled="Number(balance) < payableTotal || isProcessingOrder">
						<span v-if="isProcessingOrder" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
						{{ isProcessingOrder ? t("processing", ["checkout"]) || "Processing..." : t("continue_payment", ["checkout"]) }}
					</button>

					<!-- WeChat Payment Option -->
					<button type="button" class="btn btn-primary w-100 mt-2" v-else @click="handleCheckOutSubmit" :disabled="isProcessingOrder">
						<span v-if="isProcessingOrder" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
						{{ isProcessingOrder ? t("processing", ["checkout"]) || "Processing..." : t("continue_payment", ["checkout"]) }}
					</button>
					<p class="checkout-terms-note">
						{{ t("agree_prefix", ["checkout"]) || "By placing your order, you agree to our" }}
						<router-link to="/terms" target="_blank">{{ t("terms_conditions", ["footer"]) || "Terms & Conditions" }}</router-link>
						{{ t("and", ["checkout"]) || "and" }}
						<router-link to="/privacy" target="_blank">{{ t("privacy_policy", ["footer"]) || "Privacy Policy" }}</router-link>.
					</p>
				</div>
			</div>
		</div>
	</div>
	<button type="button" ref="showFail" class="d-none" data-bs-toggle="modal" data-bs-target="#orderFailed"></button>
	<button type="button" ref="showSuccess" class="d-none" data-bs-toggle="modal" data-bs-target="#orderSuccess"></button>

	<!-- Waiting for Payment Result Overlay -->
	<div v-if="waitingForPaymentResult" class="payment-waiting-overlay">
		<div class="payment-waiting-content">
			<div class="spinner-border text-primary mb-3" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			<h5>{{ t("processing_payment", ["checkout"]) || "Processing Payment..." }}</h5>
			<p>{{ t("complete_payment_in_new_tab", ["checkout"]) || "Please complete your payment in the new tab/window that opened." }}</p>
			<p class="text-muted">{{ t("do_not_close_this_page", ["checkout"]) || "Do not close this page until payment is complete." }}</p>
		</div>
	</div>

	<AddAddress />
</template>

<style>
div#app-note {
	padding-top: 20px;
}

#app-restaurant {
	text-align: center;
	padding: 20px 0;
}

#app-restaurant textarea {
	width: 100%;
	height: 150px;
	border: 2px solid #ddd;
	border-radius: 5px;
	padding: 15px;
	font-size: 16px;
	background-color: #f9f9f9;
	color: #333;
	resize: none;
	transition: all 0.3s ease;
	/* Prevent horizontal overflow in textarea */
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: pre-wrap;
}

#app-restaurant textarea:focus {
	border-color: #007bff;
	background-color: #ffffff;
	outline: none;
}

.payment-icon {
	max-width: 30px;
	max-height: 30px;
	margin: 0 auto;
	display: block;
}
.payment-method label {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 15px;
}

.billing-detail p {
	font-size: 14px;
	margin-bottom: 0;
	/* Ensure billing text wraps properly */
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.billing-detail.grand-total p {
	font-weight: bold;
	font-size: 16px;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.billing-detail.detail-green p {
	color: green;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

/* Cart line edit controls */
.Item-for-checkout .ci-name { font-weight: 600; color: #26331d; font-size: 14.5px; }
.ci-qty { display: flex; align-items: center; gap: 8px; margin-top: 9px; }
.ci-step { width: 27px; height: 27px; border-radius: 7px; border: 1px solid #cdd6c0; background: #fff; color: #38581c; font-size: 16px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .15s, border-color .15s; padding: 0; }
.ci-step:hover { background: #eef4e0; border-color: #86ad3f; }
.ci-count { min-width: 22px; text-align: center; font-weight: 700; color: #2c3a24; font-size: 14px; }
.ci-remove { border: none; background: none; color: #c3c8bf; cursor: pointer; font-size: 13px; margin-left: 6px; padding: 4px; }
.ci-remove:hover { color: #b3392f; }

/* Order summary — discount + total clarity */
.billing-detail.discount-line p { color: #b23522; font-weight: 500; }
.billing-detail.discount-line .disc-amt { font-weight: 700; }
.coupon-tag { display: inline-block; background: #eef4e0; color: #38581c; font-size: 11px; font-weight: 700; padding: 1px 8px; border-radius: 999px; margin-left: 6px; letter-spacing: .3px; vertical-align: middle; }
.stack-note { background: #fdf6e6; border: 1px solid #f0e0bb; color: #7a5a10; border-radius: 8px; padding: 8px 11px; font-size: 12.5px; line-height: 1.45; margin: 6px 0; }
.free-ship { color: #446936 !important; font-weight: 800 !important; letter-spacing: .3px; }
.free-ship-nudge {
	background: #f2f7e8; color: #3c5a2e; border: 1px dashed #B6D452;
	border-radius: 8px; padding: 7px 10px; margin: 4px 0 2px;
	font-size: 13px; font-weight: 600; text-align: center;
}
.billing-detail.order-total-line { border-top: 1px solid #e6e9df; margin-top: 8px; padding-top: 11px; }
.billing-detail.order-total-line p { color: #26331d !important; font-size: 18px; font-weight: 800; }
.checkout-terms-note { font-size: 12px; color: #8a908a; text-align: center; margin: 12px 4px 0; line-height: 1.5; }
.checkout-terms-note a { color: #446936; text-decoration: underline; text-underline-offset: 2px; }
.checkout-terms-note a:hover { color: #38581c; }

button:disabled {
	background-color: #d3d3d3;
	cursor: not-allowed;
}

button {
	transition: background-color 0.3s;
}

/* Global text wrapping rules */
* {
	word-wrap: break-word;
	overflow-wrap: break-word;
}

/* Specific text wrapping for common elements */
p,
h1,
h2,
h3,
h4,
h5,
h6,
span,
div,
label,
button {
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

/* Ensure long URLs, emails, or continuous text break appropriately */
.force-wrap {
	word-break: break-all;
	overflow-wrap: break-word;
	word-wrap: break-word;
}

:root {
	--body-color: #f1f0f5;
	--divider-color: #e4e4e4;
	--card-heading-color: #333333;
	--card-location-color: #787878;
	--darkest-green-bg-color: #576d27;
	--order-background-color: #76923b;
	--light-green-color: #b3c677;
	--dark-brown-color: rgb(183, 212, 80);
	--light-brown-colo: rgb(68, 106, 55);
	--lightest-brown-colo: #f7d9ba;
}

.checkout-title-card {
	display: flex;
	align-items: center;
}

.back-button {
	font-size: 28px;
	margin-right: 0.5rem;
	color: var(--card-heading-color);
}

.checkout-title-card .outlet-detail p {
	font-size: 12px;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.store-n-diliveri {
	/* display: flex; */
}

.order-type-container button {
	width: 50%;
	font-size: 1rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.order-type-container button span:nth-child(2) {
	/* font-size: 12px; */
}

/* ------------------- addresss setion ----------------- */

.no-address-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.no-address {
	display: flex;
	align-items: center;
	color: var(--card-heading-color);
	gap: 1rem;
}

.no-address-container a {
	color: var(--order-background-color);
}

.no-address i {
	color: var(--dark-brown-color);
	font-size: 20px;
}

.address-name {
	font-size: 16px;
	font-weight: 600;
	color: var(--card-heading-color);
	/* Ensure address names wrap properly */
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
	line-height: 1.4;
}

.address-description {
	font-size: 14px;
	color: var(--card-location-color);
	/* Ensure address descriptions wrap properly */
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
	line-height: 1.4;
}

.address-radio {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	row-gap: 1rem;
}

.address-radio .btn-check:checked + .btn {
	background-color: white;
	margin-right: 1rem;
	text-align: left;
	border-color: var(--dark-brown-color);
	/* Ensure button text wraps */
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
}

.address-radio .btn-check + .btn {
	background-color: white;
	margin-right: 1rem;
	text-align: left;
	border: 2px solid var(--body-color);
	/* Ensure button text wraps */
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
}

.add-addrss-button {
	padding: 16px 16px;
	height: 100%;
	width: fit-content;
	text-align: center;
	background-color: var(--order-background-color);
	/* text-align:left; */
	border: 2px solid var(--order-background-color);
	color: white;
	display: flex;
	gap: 10px;
	height: 100%;
	font-size: 1rem;
	border-radius: 0.375rem;
	/* Ensure add address button text wraps */
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
}

a.add-addrss-button {
	cursor: pointer;
}

.Item-for-checkout {
	display: flex;
	align-items: start;
	justify-content: space-between;
	/* margin-bottom: 1rem; */
	padding: 1rem 0;
	border-bottom: 1px dashed var(--divider-color);
}

.Item-for-checkout p {
	/* Ensure item names and descriptions wrap properly */
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
	line-height: 1.4;
}

.item-type-hd {
	/* Specific styling for item type headers */
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

.price-n-item-option {
	display: flex;
	gap: 1rem;
	align-items: center;
	flex-shrink: 0; /* Prevent price section from shrinking */
}

.price-n-item-option p {
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: nowrap; /* Keep prices on one line if possible */
}

.offer-card-container {
}

.coupon-card {
	padding: 10px;
	display: flex;
	align-items: flex-start;
	gap: 10px;
}

.coupon-card .coupon-icon {
	color: var(--dark-brown-color);
}

.coupon-description {
	display: block;
	font-size: 10px;
	column-rule-color: var(--card-location-color);
	/* Ensure coupon descriptions wrap */
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

.coupon-content {
	font-size: 12px;
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

.apply-coupon {
	color: var(--order-background-color);
	font-size: 12px;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.delivery-option {
	display: flex;
	align-items: center;
	gap: 10px;
}

.delivery-option .btn-check:checked + .btn {
	background-color: var(--order-background-color);
	border-color: var(--order-background-color);
	color: white;
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
}

.delivery-option .btn-check:checked + .btn:hover {
	background-color: var(--order-background-color);
	border-color: var(--order-background-color);
	color: white;
}

/* Each address occupies a full-width row so its clickable label matches the visible box
   (previously the label collapsed to a narrow strip and clicks on the address missed). */
.address-radio > div { width: 100%; }
.delivery-option .btn-check + .btn {
	background-color: transparent;
	color: var(--order-background-color);
	border-color: var(--order-background-color);
	padding: 0.75rem 2rem;
	border-radius: 10px;
	width: 100%;
	display: block;
	text-align: left;
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
}

.delivery-option .btn-check + .btn:hover {
	background-color: var(--order-background-color);
	color: white !important;
	color: var(--order-background-color);
	border-color: var(--order-background-color);
}

.biling-details-container {
}

.billing-detail {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 10px;
}

.billing-detail p {
	width: fit-content;
	color: var(--card-location-color);
	font-size: 15px;
}

.detail-green p {
	font-size: 16px;
	color: var(--dark-brown-color);
}

.grand-total {
	font-weight: bold;
	color: var(--card-heading-color);
}

.outlet-with-billing-add {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	flex-wrap: wrap;
}

.outlet-with-billing-add .billing-addshown {
	padding: 0.5rem;
}

.outlet-with-billing-add .billing-addshown p {
	font-size: 12px;
	font-weight: 600;
	color: var(--card-location-color);
	margin-bottom: 0;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.outlet-with-billing-add .billing-addshown h6 {
	font-size: 14px;
	color: var(--card-heading-color);
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
	line-height: 1.4;
}

.payment-method {
	display: flex;
}

.payment-method .btn-check + .btn {
	width: fit-content !important;
	padding: 2px 10px !important;
	height: 36px;
	color: #d2d2d2 !important;
}

.payment-method .btn-check:checked + .btn {
	width: fit-content !important;
	background-color: #d2d2d2 !important;
	border: 2px solid #d2d2d2 !important;
	color: var(--card-heading-color) !important;
	padding: 2px 10px !important;
	height: 36px;
}

.btn-primary:disabled {
	background-color: var(--light-green-color) !important;
	border: 1px solid var(--light-green-color);
}
.btn-primary:disabled:hover {
	background-color: var(--light-green-color) !important;
}

/* Alert and warning text wrapping */
.alert {
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

/* Form input and textarea text wrapping */
.form-control,
.form-select,
input,
textarea {
	word-wrap: break-word;
	overflow-wrap: break-word;
}

/* Button text wrapping for various button types */
.btn {
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: normal;
	text-align: center;
}

/* Ensure spinner and loading text stay inline */
.spinner-border {
	flex-shrink: 0;
}

@media screen and (max-width: 767.91px) {
	.address-radio .btn-check:checked + .btn {
		width: 100%;
	}

	.address-radio .btn-check + .btn {
		width: 100%;
	}

	.price-n-item-option {
		flex-direction: column;
		align-items: flex-start;
	}

	.price-n-item-option p {
		white-space: normal; /* Allow prices to wrap on mobile */
	}

	.Item-for-checkout {
		align-items: flex-start;
	}

	/* Enhanced mobile text wrapping */
	.address-name,
	.address-description {
		word-break: break-word;
		overflow-wrap: break-word;
	}

	.checkout-title-card .outlet-detail h5 {
		word-wrap: break-word;
		overflow-wrap: break-word;
		line-height: 1.3;
	}
}

/* Payment timeout warning styles */
.alert.alert-success {
	font-size: 12px;
	padding: 8px 12px;
	margin-bottom: 15px;
	border-radius: 6px;
	background-color: #d1e7dd;
	border-color: #badbcc;
	color: #0f5132;
}

.alert.alert-success i {
	color: #198754;
}

/* Mobile responsive for payment timeout warning */
@media (max-width: 768px) {
	.alert.alert-success {
		font-size: 11px;
		padding: 6px 10px;
		margin-bottom: 12px;
	}

	.alert.alert-success small {
		font-size: 11px;
		line-height: 1.4;
		word-break: break-word;
	}
}

/* Saved Payment Methods Styles */
.saved-payment-methods {
	margin-bottom: 1rem;
}

.saved-cards {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.saved-card {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem;
	border: 2px solid #e4e4e4;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: white;
}

.saved-card:hover {
	border-color: var(--order-background-color);
}

.saved-card.active {
	border-color: var(--order-background-color);
	background-color: #f8f9fa;
}

.card-info {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.card-brand {
	font-weight: 600;
	color: var(--dark-brown-color);
	font-size: 0.9rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.card-last4 {
	color: var(--card-heading-color);
	font-family: monospace;
	font-size: 1rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.card-expiry {
	color: var(--card-location-color);
	font-size: 0.8rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.card-actions {
	display: flex;
	align-items: center;
}

.default-badge {
	background-color: var(--order-background-color);
	color: white;
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	font-size: 0.75rem;
	font-weight: 500;
	word-wrap: break-word;
	overflow-wrap: break-word;
	white-space: nowrap;
}

.new-card-form {
	padding: 1rem;
	border: 1px solid #e4e4e4;
	border-radius: 8px;
	background-color: #f8f9fa;
}

.form-check {
	margin-top: 1rem;
}

.form-check-input:checked {
	background-color: var(--order-background-color);
	border-color: var(--order-background-color);
}

.form-check-label {
	font-size: 0.9rem;
	color: var(--card-heading-color);
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

/* Can be removed style */
.card {
	border-radius: 8px;
}
.card-title {
	font-weight: bold;
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}
.card-body {
	display: flex;
	flex-direction: column;
	align-items: start;
}

/* Payment Waiting Overlay Styles */
.payment-waiting-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.7);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9999;
	backdrop-filter: blur(3px);
}

.payment-waiting-content {
	background: white;
	padding: 2rem;
	border-radius: 15px;
	text-align: center;
	max-width: 400px;
	margin: 1rem;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
	animation: fadeInScale 0.3s ease-out;
}

.payment-waiting-content h5 {
	color: var(--card-heading-color);
	margin-bottom: 1rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.payment-waiting-content p {
	color: var(--card-location-color);
	margin-bottom: 0.5rem;
	word-wrap: break-word;
	overflow-wrap: break-word;
	line-height: 1.5;
}

.payment-waiting-content .text-muted {
	font-size: 0.9rem;
	color: #6c757d !important;
}

.payment-waiting-content .spinner-border {
	width: 3rem;
	height: 3rem;
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive design for mobile */
@media screen and (max-width: 767.91px) {
	.payment-waiting-content {
		margin: 1rem;
		padding: 1.5rem;
		max-width: 90%;
	}

	.payment-waiting-content h5 {
		font-size: 1.1rem;
	}

	.payment-waiting-content p {
		font-size: 0.9rem;
	}
}

/* Guest checkout gate */
.guest-gate { display: flex; justify-content: center; padding: 30px 16px 60px; }
.gg-card { background: #fff; border: 1px solid #e7eadf; border-radius: 16px; padding: 26px 24px; max-width: 440px; width: 100%; box-shadow: 0 6px 24px rgba(40,60,25,0.08); }
.gg-title { font-size: 21px; font-weight: 800; color: #26331d; margin: 0 0 6px; }
.gg-sub { font-size: 13.5px; color: #6a7360; line-height: 1.5; margin: 0 0 18px; }
.gg-sub b { color: #446936; }
.gg-in { width: 100%; border: 1px solid #dfe1da; border-radius: 10px; padding: 12px 14px; font-size: 15px; margin-bottom: 10px; box-sizing: border-box; }
.gg-in:focus { outline: none; border-color: #86ad3f; }
.gg-err { color: #a32d2d; font-size: 13px; margin: 4px 0 10px; }
.gg-btn { width: 100%; background: #446936; color: #fff; border: none; border-radius: 12px; padding: 14px; font-size: 16px; font-weight: 800; cursor: pointer; margin-top: 4px; }
.gg-btn:disabled { opacity: .6; cursor: default; }
.gg-fine { text-align: center; font-size: 11.5px; color: #9aa48c; margin: 12px 0 0; }
</style>
